/*
 * Product cards ("Pick your pour").
 *
 * Two small custom elements:
 *   <product-card-variants>  serving-size pills -> variant id, price, availability
 *   <product-card-scroller>  prev/next arrows for the card row
 *
 * Availability rules follow the general failsafe (Topic 1): sold-out variants
 * cannot be selected, the default selection is the first AVAILABLE variant, and
 * the submit button stays disabled whenever the current selection is unavailable.
 */
(function () {
  'use strict';

  class ProductCardVariants extends HTMLElement {
    constructor() {
      super();
      this.inputs = Array.from(this.querySelectorAll('input[type="radio"]'));
      this.idInput = this.querySelector('input[name="id"]');
      this.priceEl = this.querySelector('[data-price]');
      this.unitEl = this.querySelector('[data-unit-price]');
      this.submit = this.querySelector('[type="submit"]');
      this.submitTextDesktop =
        this.submit && this.submit.querySelector('.pc-card__submit-text--desktop');
      this.submitTextMobile =
        this.submit && this.submit.querySelector('.pc-card__submit-text--mobile');
      /* Legacy single-label markup (no --desktop/--mobile split). */
      this.submitText =
        this.submitTextDesktop ||
        (this.submit && this.submit.querySelector('.pc-card__submit-text'));
      this.soldOutLabel = this.dataset.soldOutLabel || 'Sold out';
      this.addLabel = this.dataset.addLabel || 'Add';
      this.addLabelMobile = this.dataset.addLabelMobile || this.addLabel;

      if (!this.inputs.length) return;

      this.inputs.forEach((input) => input.addEventListener('change', () => this.onChange(input)));

      // Never leave a sold-out variant selected on load.
      const checked = this.inputs.find((i) => i.checked && !i.disabled);
      const fallback = checked || this.inputs.find((i) => !i.disabled);
      if (fallback) {
        fallback.checked = true;
        this.onChange(fallback);
      } else {
        this.setUnavailable();
      }
    }

    onChange(input) {
      if (input.disabled) return;
      if (this.idInput) this.idInput.value = input.value;
      if (this.priceEl && input.dataset.price) this.priceEl.textContent = input.dataset.price;
      if (this.unitEl) this.unitEl.textContent = input.dataset.unitPrice || '';
      this.setAvailable(input.dataset.available === 'true');
    }

    setAvailable(available) {
      if (!this.submit) return;
      this.submit.disabled = !available;
      const desktopLabel = available ? this.addLabel : this.soldOutLabel;
      const mobileLabel = available ? this.addLabelMobile : this.soldOutLabel;
      if (this.submitTextDesktop) {
        this.submitTextDesktop.textContent = desktopLabel;
      } else if (this.submitText) {
        this.submitText.textContent = desktopLabel;
      }
      if (this.submitTextMobile) this.submitTextMobile.textContent = mobileLabel;
    }

    setUnavailable() {
      this.setAvailable(false);
    }
  }

  if (!customElements.get('product-card-variants')) {
    customElements.define('product-card-variants', ProductCardVariants);
  }

  class ProductCardScroller extends HTMLElement {
    constructor() {
      super();
      this.track = this.querySelector('[data-track]');
      this.prev = this.querySelector('[data-prev]');
      this.next = this.querySelector('[data-next]');
      if (!this.track || !this.next) return;

      this.prev.addEventListener('click', () => this.scrollBy(-1));
      this.next.addEventListener('click', () => this.scrollBy(1));
      this.track.addEventListener('scroll', () => this.update(), { passive: true });

      this.resizeObserver = new ResizeObserver(() => this.update());
      this.resizeObserver.observe(this.track);
      this.update();
    }

    get step() {
      const card = this.track.querySelector('.pc-card');
      if (!card) return this.track.clientWidth;
      const styles = getComputedStyle(this.track);
      return card.getBoundingClientRect().width + (parseFloat(styles.columnGap) || 0);
    }

    scrollBy(direction) {
      this.track.scrollBy({ left: this.step * direction, behavior: 'smooth' });
    }

    update() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      // 1px tolerance: fractional layout widths never land exactly on the ends.
      this.prev.disabled = this.track.scrollLeft <= 1;
      this.next.disabled = this.track.scrollLeft >= max - 1;
    }
  }

  if (!customElements.get('product-card-scroller')) {
    customElements.define('product-card-scroller', ProductCardScroller);
  }

  /*
   * Reveal state is an explicit class rather than a bare CSS :hover rule.
   * Driving it from JS keeps it deterministic and testable, and makes the
   * "only the pointed-at card opens" guarantee explicit instead of relying on
   * how :hover interacts with the surrounding flex layout.
   * Pointer only: touch devices keep the buy controls permanently visible,
   * which is what the mobile design shows.
   */
  function initReveal() {
    const canHover = window.matchMedia('(hover: hover) and (pointer: fine)');

    const goesToArrow = (node) => !!(node && node.closest && node.closest('.pc__arrow'));
    const goesToOpenCard = (node) => !!(node && node.closest && node.closest('.pc-card--open'));

    document.querySelectorAll('.pc--reveal-on-hover').forEach((section) => {
      const viewport = section.querySelector('product-card-scroller') || section.querySelector('.pc__viewport');

      section.querySelectorAll('.pc-card').forEach((card) => {
        if (card.dataset.revealBound) return;
        card.dataset.revealBound = 'true';

        const reveal = card.querySelector('.pc-card__reveal');
        if (!reveal) return;

        const track = card.closest('[data-track]');
        let armed = false;

        const open = () => {
          if (!armed || !canHover.matches) return;
          card.classList.add('pc-card--open');
          if (track) track.classList.add('is-revealing');
          reveal.style.maxHeight = reveal.scrollHeight + 'px';
        };
        const close = () => {
          card.classList.remove('pc-card--open');
          if (track && !track.querySelector('.pc-card--open')) {
            track.classList.remove('is-revealing');
          }
          if (canHover.matches) reveal.style.maxHeight = '0px';
        };

        card.pcRevealClose = close;

        if (canHover.matches) reveal.style.maxHeight = '0px';

        armed = true;
        card.addEventListener('pointerenter', open);
        card.addEventListener('pointerleave', (e) => {
          /* Arrow sits on top of the first/last card — leaving onto it is not a close. */
          if (goesToArrow(e.relatedTarget)) return;
          close();
        });
        card.addEventListener('focusin', (e) => {
          if (e.target.matches && e.target.matches(':focus-visible')) open();
        });
        card.addEventListener('focusout', (e) => {
          if (!card.contains(e.relatedTarget) && !goesToArrow(e.relatedTarget)) close();
        });
      });

      if (!viewport || viewport.dataset.arrowRevealBound) return;
      viewport.dataset.arrowRevealBound = 'true';
      viewport.querySelectorAll('.pc__arrow').forEach((arrow) => {
        arrow.addEventListener('pointerleave', (e) => {
          if (goesToArrow(e.relatedTarget) || goesToOpenCard(e.relatedTarget)) return;
          section.querySelectorAll('.pc-card--open').forEach((openCard) => {
            if (typeof openCard.pcRevealClose === 'function') openCard.pcRevealClose();
          });
        });
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initReveal);
  } else {
    initReveal();
  }

  // Section re-renders in the theme editor need re-binding.
  document.addEventListener('shopify:section:load', initReveal);
})();
